import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * @typedef {Object} SectionProps
 * @property {React.ReactNode} children - Section content
 * @property {string} [component] - HTML element to render (default: 'section')
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [sx] - Additional Material-UI sx props
 * @property {string} [id] - Section ID for navigation
 * @property {boolean} [dark] - Whether to use dark background (default: false)
 */

/**
 * Section component for consistent page section layouts
 *
 * A reusable section wrapper that provides consistent spacing,
 * styling, and structure for page sections. Supports both light
 * and dark background variants.
 *
 * @param {SectionProps} props - Component props
 * @returns {JSX.Element} Section wrapper component
 */
function Section({
  children,
  component = 'section',
  className,
  sx = {},
  id,
  dark = false,
}) {
  const theme = useTheme();

  return (
    <Box
      component={component}
      className={className}
      id={id}
      sx={{
        py: { xs: 6, md: 8, lg: 10 },
        px: { xs: 2, md: 3 },
        backgroundColor: dark ? theme.palette.primary.main : '#f7fafa',
        color: dark ? '#ffffff' : theme.palette.primary.main,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default Section;
